/**
 * BulkPriceDialog.jsx — small dialog for the Inventory bulk price-tier
 * editor. Lets the user pick one of 3 modes (set absolute, percent delta,
 * absolute delta) and apply to N selected SKUs in one POST.
 *
 * Props:
 *   - open: boolean
 *   - onOpenChange: (boolean) => void
 *   - count: number of selected SKUs
 *   - onApply: ({mode, value}) => Promise<void>
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CurrencyDollar, Percent, ArrowsLeftRight } from '@phosphor-icons/react';

const MODES = [
  { key: 'set', label: 'Set price to', icon: CurrencyDollar, prefix: '$', suffix: '', placeholder: '9.99', helper: 'Every selected SKU becomes this exact price.' },
  { key: 'percent_delta', label: 'Adjust by percent', icon: Percent, prefix: '', suffix: '%', placeholder: '+10', helper: 'New = current × (1 + value/100). Use negative for discount.' },
  { key: 'absolute_delta', label: 'Adjust by amount', icon: ArrowsLeftRight, prefix: '$', suffix: '', placeholder: '+1.50', helper: 'New = current + value. Use negative to drop.' },
];

export default function BulkPriceDialog({ open, onOpenChange, count, onApply }) {
  const [mode, setMode] = useState('percent_delta');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  // Reset on each open so a stale value never bleeds across promotions
  useEffect(() => { if (open) { setMode('percent_delta'); setValue(''); } }, [open]);

  const active = MODES.find((m) => m.key === mode);

  const submit = async () => {
    const num = parseFloat(value);
    if (!Number.isFinite(num)) return;
    setBusy(true);
    try {
      await onApply({ mode, value: num });
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="bulk-price-dialog">
        <DialogHeader>
          <DialogTitle>Bulk price update — {count} SKU{count === 1 ? '' : 's'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-1 p-1 bg-muted rounded-md">
            {MODES.map((m) => {
              const Icon = m.icon;
              const isActive = mode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  data-testid={`bulk-price-mode-${m.key}`}
                  className={`py-2 text-[10px] font-bold uppercase tracking-wider rounded transition flex flex-col items-center gap-1 ${
                    isActive ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon size={14} weight="fill" />
                  {m.label}
                </button>
              );
            })}
          </div>

          <div>
            <Label className="overline">Value</Label>
            <div className="mt-1 flex items-center gap-2">
              {active.prefix && (
                <span className="text-xl font-mono font-bold text-muted-foreground select-none">{active.prefix}</span>
              )}
              <Input
                type="number"
                step="0.01"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={active.placeholder}
                className="font-mono text-lg h-12"
                data-testid="bulk-price-value-input"
                autoFocus
              />
              {active.suffix && (
                <span className="text-xl font-mono font-bold text-muted-foreground select-none">{active.suffix}</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">{active.helper}</p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-800" data-testid="bulk-price-warn">
            Result is rounded to 2 decimals and clamped at $0.00 — prices never go negative.
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || value === '' || !Number.isFinite(parseFloat(value))}
            data-testid="bulk-price-apply-btn"
            className="bg-brand hover:bg-brand/90"
          >
            {busy ? 'Applying…' : `Apply to ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
